import { Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { LoadingScreen } from '@/components/loading-screen';
import { Button } from '@/components/ui/button';

import { GroundTruthNoAccess, NO_ACCESS_TITLE } from './GroundTruthNoAccess';
import { useDocumentTitle } from './useDocumentTitle';
import { UnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { useGroundTruthAccess } from './useGroundTruthAccess';

/**
 * Shared guarded shell for the GT layer-editor routes.
 *
 * Centralises the three things every editor route needs: the access guard
 * (component-body redirect, mirroring `routes/meta-search.tsx`), a back-link to
 * the overview, and a stable full-height column the editor fills. Keeping it
 * here lets the route files stay thin (param read + render).
 */
export function GroundTruthEditorShell(props: {
    title: string;
    children: React.ReactNode;
}) {
    return (
        <UnsavedChangesGuard stayIsPrimary>
            <GroundTruthEditorShellContent {...props} />
        </UnsavedChangesGuard>
    );
}

function GroundTruthEditorShellContent({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) {
    const { canAccess, isLoading } = useGroundTruthAccess();
    useDocumentTitle(!isLoading && !canAccess ? NO_ACCESS_TITLE : title);

    if (isLoading) return <LoadingScreen />;

    if (!canAccess) return <GroundTruthNoAccess />;

    return (
        <main className="flex flex-col h-screen overflow-x-hidden bg-white">
            <div className="flex items-center gap-3 px-6 py-3 border-b shrink-0">
                <Button asChild variant="ghost" size="sm" className="-ml-2">
                    <Link to="/ground-truth">
                        <ArrowLeft className="h-4 w-4 mr-1" /> Overview
                    </Link>
                </Button>
                <h1 className="text-sm font-semibold text-gray-900">{title}</h1>
            </div>
            <div className="flex-1 min-h-0 min-w-0">{children}</div>
        </main>
    );
}
